import {
  PREVIEW_ATTRIBUTE,
  PREVIEW_OBSERVER_DEBOUNCE_MS,
  PREVIEW_SELECTOR,
} from "./constants";
import { resolvePreviewTarget } from "./target";
import type { ResolvedPreviewTarget } from "./types";

export interface ScanAndAttachOptions {
  root?: Document | HTMLElement;
  attach: (element: HTMLElement, target: ResolvedPreviewTarget) => void;
  detach: (element: HTMLElement) => void;
}

function isPreviewElement(node: Node): node is HTMLElement {
  return node instanceof HTMLElement && node.matches(PREVIEW_SELECTOR);
}

function collectPreviewElements(node: Node): HTMLElement[] {
  if (!(node instanceof HTMLElement)) {
    return [];
  }

  const elements = node.matches(PREVIEW_SELECTOR) ? [node] : [];
  elements.push(
    ...Array.from(node.querySelectorAll<HTMLElement>(PREVIEW_SELECTOR)),
  );
  return elements;
}

export function scanAndAttach({
  root = document,
  attach,
  detach,
}: ScanAndAttachOptions): () => void {
  const attachedElements = new Set<HTMLElement>();
  const attachedKeys = new Map<HTMLElement, string>();
  const queuedAddedNodes = new Set<Node>();
  const queuedRemovedNodes = new Set<Node>();
  let debounceTimer: number | undefined;

  const bindElement = (element: HTMLElement): void => {
    const rawValue = element.dataset.librariumPreview;
    if (!rawValue) {
      unbindElement(element);
      return;
    }

    const target = resolvePreviewTarget(rawValue);
    if (!target) {
      detach(element);
      attachedElements.delete(element);
      return;
    }

    if (attachedKeys.get(element) === target.cacheKey) {
      return;
    }

    if (attachedElements.has(element)) {
      detach(element);
    }

    attach(element, target);
    attachedElements.add(element);
    attachedKeys.set(element, target.cacheKey);
  };

  const unbindElement = (element: HTMLElement): void => {
    if (!attachedElements.has(element)) {
      return;
    }

    detach(element);
    attachedElements.delete(element);
    attachedKeys.delete(element);
  };

  const scanNode = (node: Node): void => {
    for (const element of collectPreviewElements(node)) {
      bindElement(element);
    }
  };

  const removeNode = (node: Node): void => {
    for (const element of collectPreviewElements(node)) {
      unbindElement(element);
    }
  };

  const flushMutationQueue = (): void => {
    debounceTimer = undefined;

    for (const node of queuedRemovedNodes) {
      removeNode(node);
    }

    for (const node of queuedAddedNodes) {
      scanNode(node);
    }

    queuedRemovedNodes.clear();
    queuedAddedNodes.clear();
  };

  const scheduleMutationFlush = (): void => {
    if (debounceTimer !== undefined) {
      return;
    }

    debounceTimer = window.setTimeout(
      flushMutationQueue,
      PREVIEW_OBSERVER_DEBOUNCE_MS,
    );
  };

  if (root instanceof HTMLElement) {
    scanNode(root);
  } else {
    for (const element of Array.from(
      root.querySelectorAll<HTMLElement>(PREVIEW_SELECTOR),
    )) {
      bindElement(element);
    }
  }

  const observerTarget =
    root instanceof HTMLElement ? root : root.documentElement;

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (
        record.type === "attributes" &&
        record.target instanceof HTMLElement
      ) {
        bindElement(record.target);
        continue;
      }

      for (const node of Array.from(record.removedNodes)) {
        queuedRemovedNodes.add(node);
      }

      for (const node of Array.from(record.addedNodes)) {
        queuedAddedNodes.add(node);
      }
    }

    scheduleMutationFlush();
  });

  observer.observe(observerTarget, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [PREVIEW_ATTRIBUTE],
  });

  return () => {
    observer.disconnect();

    if (debounceTimer !== undefined) {
      window.clearTimeout(debounceTimer);
    }

    for (const element of Array.from(attachedElements)) {
      unbindElement(element);
    }
  };
}
